import { create } from "zustand";
import {
  sanitizeHero,
  defaultHero,
  parseParts,
  stringifyParts,
  DEFAULT_PARTS,
  HERO_CATALOG,
  type SavedHero,
  type HeroParts,
  type PartCategory,
  type PaletteId,
  type HeroBase,
} from "@greg-chick/shared";
import {
  loadHero,
  saveHero,
  clearHero,
  heroFromCurrentUrl,
} from "../lib/heroStorage";
import { postHero } from "../lib/heroApi";

/**
 * 🔴 **코드를 두 번 받아 오지 않는다.**
 *
 * `syncCode` 는 서버에 아이를 **새로 만든다**. 부르는 곳이 셋이고(랠리·그림책·꾸미기)
 * 전부 「코드가 없으면」 조건의 `useEffect` 다. 두 곳이 같이 떠 있으면 둘 다 부르고,
 * 그러면 **한 아이에게 코드가 둘** 생긴다 — 나중 것만 기기에 남고 먼저 것은 아무도 모르는 채
 * 서버에 남는다. 아이는 자기 그림책이 어느 코드에 붙었는지 모른다.
 *
 * 개발 중에는 더 잦다: `React.StrictMode` 가 효과를 두 번 태운다(`main.tsx`).
 * 운영 D1 을 보며 개발하면 그 찌꺼기가 그대로 쌓인다(2026-08-30 에 한 줄 지웠다).
 *
 * 그래서 **부르는 중이면 그 답을 같이 기다린다**. 끝나면 풀어서 다음 시도는 다시 보낸다
 * (한 번 실패했다고 영영 못 받으면 그게 더 나쁘다).
 */
let codeInFlight: Promise<string | null> | null = null;

interface HeroState {
  /** 커스터마이저가 편집 중인 작업본(항상 유효값). */
  draft: SavedHero;
  /** localStorage 에 저장된 적이 있는가(재방문자 판별, FR-HERO-04). */
  saved: boolean;
  /** 초기 1회 로드 완료 여부. */
  hydrated: boolean;

  hydrate: () => void;
  parts: () => HeroParts;
  setBase: (base: HeroBase) => void;
  /** 카테고리(body/head/face/hair/outfit/acc) 옵션 변경. */
  setPart: (cat: PartCategory, id: string) => void;
  /** 모든 카테고리를 랜덤 조합(완주 한정 a4 제외). */
  randomize: () => void;
  setPalette: (palette: PaletteId) => void;
  setNickname: (nickname: string) => void;
  loadFromHero: (hero: SavedHero) => void;
  /** 작업본을 localStorage 에 커밋(FR-HERO-03). 저장된 히어로 반환. */
  commit: () => SavedHero;
  /** 엣지에 동기화해 익명 코드 발급/갱신(best-effort). 성공 시 code 저장(FR-HERO-09/12). */
  syncCode: () => Promise<string | null>;
  /**
   * 👋 **기기를 비운다** — 학교 태블릿을 다음 아이에게 넘길 때.
   *
   * 서버에 있는 히어로는 그대로 둔다. 코드만 있으면 언제든 돌아온다 —
   * 지우는 게 아니라 **이 기기에서 내려놓는** 것이다.
   */
  handoff: () => void;
}

function withParts(draft: SavedHero, patch: Partial<HeroParts>): string {
  const cur = parseParts(draft.parts) ?? DEFAULT_PARTS;
  return stringifyParts({ ...cur, ...patch });
}

function randomParts(): HeroParts {
  const pick = <T extends readonly string[]>(arr: T): T[number] =>
    arr[Math.floor(Math.random() * arr.length)] as T[number];
  return {
    body: pick(HERO_CATALOG.body),
    hair: pick(HERO_CATALOG.hair),
    outfit: pick(HERO_CATALOG.outfit),
    acc: pick(HERO_CATALOG.acc.filter((a) => a !== "a4")), // 완주 한정 제외
  };
}

export const useHeroStore = create<HeroState>((set, get) => ({
  draft: defaultHero(),
  saved: false,
  hydrated: false,

  hydrate: () => {
    if (get().hydrated) return;
    // 우선순위: ?h= 공유 링크(FR-HERO-13) > localStorage(FR-HERO-04) > 기본
    const fromUrl = heroFromCurrentUrl();
    const stored = loadHero();
    const hero = fromUrl ?? stored;
    set({
      draft: hero ?? defaultHero(),
      saved: stored !== null,
      hydrated: true,
    });
  },

  parts: () => parseParts(get().draft.parts) ?? DEFAULT_PARTS,

  setBase: (base) => set((s) => ({ draft: { ...s.draft, base } })),
  setPart: (cat, id) =>
    set((s) => ({
      draft: { ...s.draft, parts: withParts(s.draft, { [cat]: id } as Partial<HeroParts>) },
    })),
  randomize: () =>
    set((s) => {
      // 직접 꾸미기(custom)면 파츠만 무작위(모드 유지). 완성형 프리셋이면 탐험가 중 무작위.
      const presets = HERO_CATALOG.base.filter((b) => b !== "custom");
      const base =
        s.draft.base === "custom"
          ? "custom"
          : (presets[Math.floor(Math.random() * presets.length)] as HeroBase);
      return { draft: { ...s.draft, base, parts: stringifyParts(randomParts()) } };
    }),
  setPalette: (palette) => set((s) => ({ draft: { ...s.draft, palette } })),
  setNickname: (nickname) =>
    set((s) => ({ draft: { ...s.draft, nickname } })),

  loadFromHero: (hero) =>
    set({ draft: sanitizeHero(hero), saved: true, hydrated: true }),

  commit: () => {
    const saved = saveHero(get().draft);
    set({ draft: saved, saved: true });
    return saved;
  },

  handoff: () => {
    clearHero();
    set({ draft: defaultHero(), saved: false, hydrated: true });
  },

  syncCode: async () => {
    if (codeInFlight) return codeInFlight; // 이미 받아 오는 중 — 같은 답을 기다린다
    codeInFlight = (async () => {
      const posted = get().draft; // 보낸 스냅샷 고정(대기 중 편집과 분리)
      const result = await postHero(posted);
      if (!result?.code) return null;
      // 로컬엔 '보낸 스냅샷 + code'만 저장 — 응답 대기 중 사용자가 편집한 라이브 draft 를 무단 저장하지 않는다.
      saveHero({ ...posted, code: result.code });
      // 메모리 draft 에는 code 만 병합(진행 중 편집 보존, 스냅샷으로 덮어쓰지 않음).
      set((s) => ({ draft: { ...s.draft, code: result.code } }));
      return result.code;
    })();
    try {
      return await codeInFlight;
    } finally {
      codeInFlight = null; // 🔴 실패해도 푼다 — 한 번 못 받았다고 영영 못 받으면 안 된다
    }
  },
}));
